import type { ActionLogEntry, TerminalStore } from "../types";

/**
 * A tiny pub/sub that carries dispatched Redux actions from
 * {@link createTerminalMiddleware} to any terminal listening on the same
 * channel. Decoupled on purpose: the middleware lives in the store, the
 * terminal lives in the view tree, and neither imports the other.
 */

type Listener = (entry: ActionLogEntry) => void;

const channels = new Map<string, Set<Listener>>();

/** Marker the terminal stamps on actions it dispatches, read by the middleware. */
export const TERMINAL_ORIGIN_TAG = "__rtOrigin";

export function subscribeActions(channel: string, fn: Listener): () => void {
  let set = channels.get(channel);
  if (!set) {
    set = new Set();
    channels.set(channel, set);
  }
  set.add(fn);
  return () => {
    set?.delete(fn);
  };
}

export function emitAction(channel: string, entry: ActionLogEntry): void {
  channels.get(channel)?.forEach((fn) => fn(entry));
}

/**
 * Dispatches an action to the host store, tagged so the middleware records its
 * origin as "terminal". Safe no-op (returns false) when no store is wired.
 */
export function dispatchTerminalAction(
  store: TerminalStore | undefined,
  action: { type: string; [k: string]: unknown },
): boolean {
  if (!store) return false;
  store.dispatch({ ...action, [TERMINAL_ORIGIN_TAG]: "terminal" });
  return true;
}
